import { Injectable } from '@angular/core';
import { CatalogueTemplatePayload } from '../models/bot/catalogue';
import { DiscordEmbed } from '../components/embed-builder/embed.model';
import { EmbedService } from './embed.service';
import { EmojiComponentService } from './emojiComponent.service';

type PreviewEmojiData = {
  field_name?: string;
  type?: string;
  emojis?: { [key: string]: PreviewEmojiData };
};

type PreviewEmoji = {
  emoji: string;
  fieldName: string;
  controlName: string;
  type: string;
  subEmojis: {
    emoji: string;
    fieldName: string;
    controlName: string;
    limit: number;
  }[];
  limit: number;
};

type SampleMember = { user_id: string; display_name: string };

const SAMPLE_NAMES = [
  'Aria Stormblade',
  'Borin Ironfist',
  'Cira Moonveil',
  'Drax Flameheart',
  'Elowen Sage',
  'Fenrir Nightshade',
  'Gilda Brightshield',
  'Kael Windrunner',
  'Lyra Dawnstrike',
  'Thorne Ashwalker',
];

@Injectable({
  providedIn: 'root',
})
export class CataloguePreviewService {
  constructor(
    private embedService: EmbedService,
    private emojiComponentService: EmojiComponentService
  ) {}

  transformEmojies(
    emojies: CatalogueTemplatePayload['emojies']
  ): { [key: string]: PreviewEmojiData } {
    const result: { [key: string]: PreviewEmojiData } = {};
    for (const [key, value] of Object.entries(emojies || {})) {
      if (typeof value !== 'string') {
        continue;
      }
      const parts = value.split(',');
      const entry: PreviewEmojiData = { type: parts[0] };
      if (parts.length > 1) {
        entry.field_name = parts[1];
      }
      const nested = emojies[value];
      if (nested && typeof nested === 'object') {
        entry.emojis = {};
        for (const [subEmoji, subName] of Object.entries(nested)) {
          entry.emojis[subEmoji] = { field_name: subName };
        }
      }
      result[key] = entry;
    }
    return result;
  }

  private generateControlName(fieldName: string | undefined): string {
    return (fieldName || 'field')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_|_$/g, '');
  }

  private convertEmojisObjectToArray(
    emojis: { [key: string]: PreviewEmojiData },
    limits: CatalogueTemplatePayload['limits'] = {}
  ): PreviewEmoji[] {
    return Object.keys(emojis || {}).map((mainEmojiKey) => {
      const mainEmoji = emojis[mainEmojiKey];
      const controlName = this.generateControlName(mainEmoji.field_name);
      const mainLimitKey = `${mainEmoji.type},${mainEmoji.field_name}`;
      const subEmojis = mainEmoji.emojis
        ? Object.keys(mainEmoji.emojis).map((subEmojiKey) => {
            const subEmoji = mainEmoji.emojis[subEmojiKey];
            const subLimitKey = `${mainEmoji.type},${mainEmoji.field_name},${subEmoji.field_name}`;
            return {
              emoji: subEmojiKey,
              fieldName: subEmoji.field_name,
              controlName: this.generateControlName(subEmoji.field_name),
              limit: Number(limits?.[subLimitKey]) || 0,
            };
          })
        : [];
      return {
        emoji: mainEmojiKey,
        fieldName: mainEmoji.field_name,
        controlName,
        type: mainEmoji.type,
        subEmojis,
        limit: Number(limits?.[mainLimitKey]) || 0,
      };
    });
  }

  private unlimitedOrCap(limit: number, fallback: number): number {
    return limit > 0 ? limit : fallback;
  }

  private takeMembers(
    serverMembers: SampleMember[],
    nameIndex: { value: number },
    count: number
  ): SampleMember[] {
    const taken: SampleMember[] = [];
    for (let i = 0; i < count && nameIndex.value < serverMembers.length; i++) {
      taken.push(serverMembers[nameIndex.value]);
      nameIndex.value++;
    }
    return taken;
  }

  /**
   * Fill sample signups without exceeding general, field, or sub-field limits.
   * Parent field limits outrank sub-field limits (Tank before Tank › Magicka).
   */
  private buildSampleMembers(
    emojisArray: PreviewEmoji[],
    limits: CatalogueTemplatePayload['limits'] = {}
  ): {
    selectedMembersMap: Record<string, SampleMember[]>;
    serverMembers: SampleMember[];
  } {
    const serverMembers = SAMPLE_NAMES.map((name, index) => ({
      user_id: String(1000 + index),
      display_name: name,
    }));
    const selectedMembersMap: Record<string, SampleMember[]> = {};
    const nameIndex = { value: 0 };

    const totalCap = this.unlimitedOrCap(Number(limits.max_sign_ups_total) || 0, SAMPLE_NAMES.length);
    const roleCap = this.unlimitedOrCap(Number(limits.max_sign_ups_per_role) || 0, SAMPLE_NAMES.length);
    const statusCap = this.unlimitedOrCap(
      Number(limits.max_sign_ups_per_status) || 0,
      SAMPLE_NAMES.length
    );

    let totalRemaining = totalCap;
    let roleRemaining = roleCap;
    let statusRemaining = statusCap;

    const typeRemaining = (type: string): number => {
      if (type === 'role') {
        return Math.min(totalRemaining, roleRemaining);
      }
      if (type === 'status') {
        return Math.min(totalRemaining, statusRemaining);
      }
      return totalRemaining;
    };

    const consume = (type: string, n: number): void => {
      totalRemaining -= n;
      if (type === 'role') {
        roleRemaining -= n;
      } else if (type === 'status') {
        statusRemaining -= n;
      }
    };

    for (const emoji of emojisArray) {
      if (emoji.type !== 'role' && emoji.type !== 'status') {
        continue;
      }

      // Parent field limit has higher priority than any sub-field limit.
      let fieldRemaining = this.unlimitedOrCap(emoji.limit, SAMPLE_NAMES.length);
      fieldRemaining = Math.min(fieldRemaining, typeRemaining(emoji.type));

      selectedMembersMap[emoji.controlName] = [];

      if (!emoji.subEmojis?.length) {
        // At most 2 sample names, and never above the effective field/general caps.
        const desired = Math.min(2, fieldRemaining);
        const taken = this.takeMembers(serverMembers, nameIndex, desired);
        selectedMembersMap[emoji.controlName] = taken;
        consume(emoji.type, taken.length);
        continue;
      }

      for (const sub of emoji.subEmojis) {
        selectedMembersMap[sub.controlName] = [];
        if (fieldRemaining <= 0 || typeRemaining(emoji.type) <= 0) {
          continue;
        }
        const subCap = this.unlimitedOrCap(sub.limit, fieldRemaining);
        const desired = Math.min(1, subCap, fieldRemaining, typeRemaining(emoji.type));
        const taken = this.takeMembers(serverMembers, nameIndex, desired);
        selectedMembersMap[sub.controlName] = taken;
        fieldRemaining -= taken.length;
        consume(emoji.type, taken.length);
      }
    }

    return { selectedMembersMap, serverMembers };
  }

  renderPreview(template: CatalogueTemplatePayload, title?: string): void {
    const limits = template.limits || {};
    const emojisObj = this.transformEmojies(template.emojies);
    const emojisArray = this.convertEmojisObjectToArray(emojisObj, limits);
    const { selectedMembersMap, serverMembers } = this.buildSampleMembers(
      emojisArray,
      limits
    );

    const color =
      typeof template.color === 'number'
        ? `#${template.color.toString(16).padStart(6, '0')}`
        : template.color === 'default'
        ? '#000000'
        : String(template.color);

    const currEmbed: DiscordEmbed = {
      title: title || `${template.name || 'Event'} — Sample Raid`,
      description:
        '**Event Info:**\n🗓️ Saturday, Oct 25\n🕑 20:00 - 23:00\n\nSample preview with generated raider names.',
      color,
      fields: [],
      footerText: 'Raid Organizer catalogue preview',
    };

    this.emojiComponentService.signupTemplateEmojisToEmbedFields(
      this.embedService,
      [],
      { currEmbed },
      {
        emojis: emojisArray,
        emojiBehavior: String(template.emoji_behavior ?? 1),
        useButtons: template.use_buttons !== false,
        color,
        emojiTitle: !!template.emoji_title,
      },
      selectedMembersMap,
      serverMembers
    );
  }
}
